import React, { useState, useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Heading,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  Text,
  Progress,
  HStack,
  Badge,
} from '@chakra-ui/react';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * OverviewPanel - Summary statistics and quick insights
 */
function OverviewPanel({ poll, pollId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch progress stats
      const progressRes = await fetchWithAuth(`/api/polls/${pollId}/progress/`);
      if (!progressRes.ok) {
        throw new Error('Failed to fetch progress data');
      }
      const progressData = await progressRes.json();

      // Fetch quality summary
      const qualityRes = await fetchWithAuth(`/api/polls/${pollId}/quality-summary/`);
      const qualityData = qualityRes.ok ? await qualityRes.json() : null;

      // Fetch active weights
      const weightsRes = await fetchWithAuth(`/api/polls/${pollId}/active-weights/`);
      const weightsData = weightsRes.ok ? await weightsRes.json() : null;

      setStats({
        progress: progressData,
        quality: qualityData?.quality_summary || null,
        weights: weightsData,
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text>Loading overview...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const { progress, quality, weights } = stats || {};

  return (
    <VStack spacing={6} align="stretch">
      {/* Response Statistics */}
      <Box>
        <Heading size="md" mb={4}>Response Statistics</Heading>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Stat
            p={4}
            bg="blue.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="blue.200"
          >
            <StatLabel>Total Invitations Sent</StatLabel>
            <StatNumber>{progress?.total_sent || 0}</StatNumber>
            <StatHelpText>Survey links generated</StatHelpText>
          </Stat>

          <Stat
            p={4}
            bg="green.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="green.200"
          >
            <StatLabel>Total Responses</StatLabel>
            <StatNumber>{progress?.total_completed || 0}</StatNumber>
            <StatHelpText>
              {progress?.completion_rate || 0}% completion rate
            </StatHelpText>
          </Stat>

          <Stat
            p={4}
            bg="purple.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="purple.200"
          >
            <StatLabel>Response Goal</StatLabel>
            <StatNumber>{progress?.response_goal || 'N/A'}</StatNumber>
            <StatHelpText>
              {progress?.goal_met ? (
                <Badge colorScheme="green">Goal Met</Badge>
              ) : (
                `${progress?.progress_percentage || 0}% complete`
              )}
            </StatHelpText>
          </Stat>

          <Stat
            p={4}
            bg="orange.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="orange.200"
          >
            <StatLabel>Poll Status</StatLabel>
            <StatNumber>
              <Badge
                colorScheme={
                  poll.state === 'live' ? 'green' :
                  poll.state === 'closed' ? 'red' :
                  poll.state === 'staged' ? 'blue' :
                  'gray'
                }
                fontSize="lg"
              >
                {poll.state.toUpperCase()}
              </Badge>
            </StatNumber>
            <StatHelpText>Current state</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Box>

      {/* Progress to Goal */}
      {progress?.response_goal && (
        <Box>
          <Heading size="md" mb={4}>Progress to Goal</Heading>
          <Box
            p={4}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="medium">
                {progress.total_completed} / {progress.response_goal} responses
              </Text>
              <Text color="gray.600" fontSize="sm">
                {progress.progress_percentage}%
              </Text>
            </HStack>
            <Progress
              value={progress.progress_percentage}
              colorScheme={progress.goal_met ? 'green' : 'blue'}
              size="lg"
              borderRadius="md"
            />
          </Box>
        </Box>
      )}

      {/* Quality Metrics */}
      {quality && (
        <Box>
          <Heading size="md" mb={4}>Response Quality</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat
              p={4}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <StatLabel>High Quality Responses</StatLabel>
              <StatNumber color="green.600">
                {quality.high_quality_count || 0}
              </StatNumber>
              <StatHelpText>
                {quality.high_quality_percentage || 0}% of total
              </StatHelpText>
            </Stat>

            <Stat
              p={4}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <StatLabel>Flagged Responses</StatLabel>
              <StatNumber color="orange.600">
                {quality.flagged_count || 0}
              </StatNumber>
              <StatHelpText>
                {quality.speeders_count || 0} speeders, {quality.straight_liners_count || 0} straight-liners
              </StatHelpText>
            </Stat>

            <Stat
              p={4}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <StatLabel>Mean Quality Score</StatLabel>
              <StatNumber>{quality.mean_quality_score || 'N/A'}</StatNumber>
              <StatHelpText>Out of 100</StatHelpText>
            </Stat>
          </SimpleGrid>
        </Box>
      )}

      {/* Weighting Status */}
      <Box>
        <Heading size="md" mb={4}>Weighting Status</Heading>
        <Box
          p={4}
          bg={weights?.has_weights ? 'green.50' : 'gray.50'}
          borderRadius="md"
          borderWidth="1px"
          borderColor={weights?.has_weights ? 'green.200' : 'gray.200'}
        >
          {weights?.has_weights ? (
            <VStack align="start" spacing={2}>
              <HStack>
                <Badge colorScheme="green">Active</Badge>
                <Text fontWeight="medium">{weights.scheme_name}</Text>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                Method: {weights.method === 'raking' ? 'Raking (IPF)' : 'Post-Stratification'}
              </Text>
              {weights.design_effect && (
                <Text fontSize="sm" color="gray.600">
                  Design Effect: {weights.design_effect.design_effect} (n_effective = {weights.design_effect.n_effective})
                </Text>
              )}
            </VStack>
          ) : (
            <Text color="gray.600">
              No active weighting scheme. Visit the Weighting tab to configure demographic weights.
            </Text>
          )}
        </Box>
      </Box>

      {/* Quick Actions Info */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Next Steps</Text>
          <Text fontSize="sm" mt={1}>
            Use the tabs above to:
            • Generate <strong>Crosstabs</strong> for demographic analysis
            • Configure <strong>Weighting</strong> to adjust for sample bias
            • Review <strong>Quality</strong> metrics and flag low-quality responses
            • <strong>Export</strong> your data in CSV, Excel, SPSS, or Stata format
          </Text>
        </Box>
      </Alert>
    </VStack>
  );
}

export default OverviewPanel;
